import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Card, Button, Form as BsForm } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';

const MODO_ATUAL = 'ATUAL';
const MODO_HISTORICO = 'HISTORICO';

const validationSchema = Yup.object().shape({
  modo: Yup.string().required().oneOf([MODO_ATUAL, MODO_HISTORICO]),
  dataLimite: Yup.date()
    .nullable()
    .when('modo', {
      is: MODO_HISTORICO,
      then: (s) => s.required('Data Limite é obrigatória no modo Histórico'),
      otherwise: (s) => s.nullable(),
    }),
  dataLancamentoAntesDe: Yup.date().nullable(),
  codigoGe: Yup.number().nullable(),
  codigoGrupoGe: Yup.number().nullable(),
  codigoEmpresa: Yup.number().nullable(),
  codigoFamilia: Yup.number().nullable(),
});

const valoresIniciais = {
  filtrarPor: 'GE',
  codigoGe: null,
  codigoGrupoGe: null,
  codigoEmpresa: null,
  codigoFamilia: null,
  dataLancamentoAntesDe: '',
  modo: MODO_ATUAL,
  dataLimite: '',
};

const CamposCascata = ({ values, setFieldValue, opcoesGes, opcoesGruposGe }) => {
  const [opcoesEmpresas, setOpcoesEmpresas] = useState([]);
  const [opcoesEmpreendimentos, setOpcoesEmpreendimentos] = useState([]);

  // Cascata GE/GrupoGE → Empresa/Empreendimento com flag anti-race (LICOES ciclo 9 L3)
  useEffect(() => {
    let ativo = true;
    const filtro = {
      codigo_ge: values.codigoGe,
      codigo_grupo_ge: values.codigoGrupoGe,
    };
    if (values.codigoGe || values.codigoGrupoGe) {
      Promise.all([
        api.estImobObterEmpresas(filtro),
        api.estImobObterEmpreendimentos(filtro),
      ])
        .then(([emps, emps2]) => {
          if (!ativo) return;
          setOpcoesEmpresas(Array.isArray(emps) ? emps : []);
          setOpcoesEmpreendimentos(Array.isArray(emps2) ? emps2 : []);
        })
        .catch(() => {
          if (!ativo) return;
          toast.error('Erro ao carregar empresas/empreendimentos.');
        });
    } else {
      setOpcoesEmpresas([]);
      setOpcoesEmpreendimentos([]);
    }
    return () => {
      ativo = false;
    };
  }, [values.codigoGe, values.codigoGrupoGe]);

  const opcoesGeSelect = opcoesGes.map((g) => ({
    value: g.codigo_grupo_empresa,
    label: g.apelido,
  }));

  const opcoesGrupoGeSelect = opcoesGruposGe.map((g) => ({
    value: g.codigo,
    label: `${g.codigo} - ${g.descricao}`,
  }));

  const opcoesEmpresaSelect = opcoesEmpresas.map((e) => ({
    value: e.codigo,
    label: e.razao,
  }));

  const opcoesEmpreendimentoSelect = opcoesEmpreendimentos.map((f) => ({
    value: f.codigo_familia,
    label: f.nome_da_familia,
  }));

  const handleFiltrarPorChange = (novoFiltrarPor) => {
    setFieldValue('filtrarPor', novoFiltrarPor);
    setFieldValue('codigoGe', null);
    setFieldValue('codigoGrupoGe', null);
    setFieldValue('codigoEmpresa', null);
    setFieldValue('codigoFamilia', null);
  };

  const handleGeChange = (opcao) => {
    setFieldValue('codigoGe', opcao ? opcao.value : null);
    setFieldValue('codigoEmpresa', null);
    setFieldValue('codigoFamilia', null);
  };

  const handleGrupoGeChange = (opcao) => {
    setFieldValue('codigoGrupoGe', opcao ? opcao.value : null);
    setFieldValue('codigoEmpresa', null);
    setFieldValue('codigoFamilia', null);
  };

  const geValorSelecionado = values.codigoGe
    ? opcoesGeSelect.find((o) => o.value === values.codigoGe) || null
    : null;

  const grupoGeValorSelecionado = values.codigoGrupoGe
    ? opcoesGrupoGeSelect.find((o) => o.value === values.codigoGrupoGe) || null
    : null;

  const empresaValorSelecionado = values.codigoEmpresa
    ? opcoesEmpresaSelect.find((o) => o.value === values.codigoEmpresa) || null
    : null;

  const empreendimentoValorSelecionado = values.codigoFamilia
    ? opcoesEmpreendimentoSelect.find((o) => o.value === values.codigoFamilia) || null
    : null;

  const semGeEGrupo = !values.codigoGe && !values.codigoGrupoGe;

  return (
    <>
      <Row className="mb-2">
        <Col xs={12}>
          <BsForm.Label className="small mb-1">Filtrar por</BsForm.Label>
          <div>
            <BsForm.Check
              inline
              type="radio"
              label="GE"
              id="filtrarPor-ge"
              checked={values.filtrarPor === 'GE'}
              onChange={() => handleFiltrarPorChange('GE')}
            />
            <BsForm.Check
              inline
              type="radio"
              label="Grupo de GE"
              id="filtrarPor-grupoGe"
              checked={values.filtrarPor === 'GRUPO_GE'}
              onChange={() => handleFiltrarPorChange('GRUPO_GE')}
            />
          </div>
        </Col>
      </Row>

      <Row className="mb-2">
        {values.filtrarPor === 'GE' && (
          <Col xs={12} md={6} lg={4}>
            <BsForm.Label className="small mb-1">GE</BsForm.Label>
            <Select
              components={{ DropdownIndicator }}
              styles={customStyles}
              options={opcoesGeSelect}
              value={geValorSelecionado}
              onChange={handleGeChange}
              isClearable
              isSearchable
              placeholder="Selecione a GE..."
              noOptionsMessage={() => 'Nenhuma GE disponível'}
            />
          </Col>
        )}
        {values.filtrarPor === 'GRUPO_GE' && (
          <Col xs={12} md={6} lg={4}>
            <BsForm.Label className="small mb-1">Grupo de GE</BsForm.Label>
            <Select
              components={{ DropdownIndicator }}
              styles={customStyles}
              options={opcoesGrupoGeSelect}
              value={grupoGeValorSelecionado}
              onChange={handleGrupoGeChange}
              isClearable
              isSearchable
              placeholder="Selecione o Grupo de GE..."
              noOptionsMessage={() => 'Nenhum grupo disponível'}
            />
          </Col>
        )}

        <Col xs={12} md={6} lg={4}>
          <BsForm.Label className="small mb-1">Empresa</BsForm.Label>
          <Select
            components={{ DropdownIndicator }}
            styles={customStyles}
            options={opcoesEmpresaSelect}
            value={empresaValorSelecionado}
            onChange={(opcao) =>
              setFieldValue('codigoEmpresa', opcao ? opcao.value : null)
            }
            isClearable
            isSearchable
            isDisabled={semGeEGrupo}
            placeholder="Selecione a empresa..."
            noOptionsMessage={() => 'Nenhuma empresa disponível'}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <BsForm.Label className="small mb-1">Empreendimento</BsForm.Label>
          <Select
            components={{ DropdownIndicator }}
            styles={customStyles}
            options={opcoesEmpreendimentoSelect}
            value={empreendimentoValorSelecionado}
            onChange={(opcao) =>
              setFieldValue('codigoFamilia', opcao ? opcao.value : null)
            }
            isClearable
            isSearchable
            isDisabled={semGeEGrupo}
            placeholder="Selecione o empreendimento..."
            noOptionsMessage={() => 'Nenhum empreendimento disponível'}
          />
        </Col>
      </Row>
    </>
  );
};

const FiltrosRelatorio = ({
  onSubmit,
  isLoading,
  opcoesGes,
  opcoesGruposGe,
}) => (
  <Formik
    initialValues={valoresIniciais}
    validationSchema={validationSchema}
    onSubmit={(values) => {
      onSubmit({
        codigo_ge: values.codigoGe,
        codigo_grupo_ge: values.codigoGrupoGe,
        codigo_empresa: values.codigoEmpresa,
        codigo_familia: values.codigoFamilia,
        modo: values.modo,
        data_limite: values.dataLimite || null,
        data_lancamento_antes_de: values.dataLancamentoAntesDe || null,
      });
    }}
  >
    {({ values, errors, touched, setFieldValue, resetForm }) => {
      const handleModoChange = (novoModo) => {
        setFieldValue('modo', novoModo);
        if (novoModo === MODO_ATUAL) {
          setFieldValue('dataLimite', '');
        }
      };

      return (
        <Form>
          <Card className="mb-3">
            <Card.Header>
              <strong>Filtros</strong>
            </Card.Header>
            <Card.Body>
              <CamposCascata
                values={values}
                setFieldValue={setFieldValue}
                opcoesGes={opcoesGes}
                opcoesGruposGe={opcoesGruposGe}
              />

              <Row className="mb-2">
                <Col xs={12} md={6} lg={4}>
                  <BsForm.Label className="small mb-1">
                    Lançamento anterior a (opcional)
                  </BsForm.Label>
                  <BsForm.Control
                    type="date"
                    size="sm"
                    value={values.dataLancamentoAntesDe}
                    onChange={(e) =>
                      setFieldValue('dataLancamentoAntesDe', e.target.value)
                    }
                  />
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <BsForm.Label className="small mb-1">Modo</BsForm.Label>
                  <div>
                    <BsForm.Check
                      inline
                      type="radio"
                      label="Atual"
                      id="modo-atual"
                      checked={values.modo === MODO_ATUAL}
                      onChange={() => handleModoChange(MODO_ATUAL)}
                    />
                    <BsForm.Check
                      inline
                      type="radio"
                      label="Histórico"
                      id="modo-historico"
                      checked={values.modo === MODO_HISTORICO}
                      onChange={() => handleModoChange(MODO_HISTORICO)}
                    />
                  </div>
                </Col>

                {values.modo === MODO_HISTORICO && (
                  <Col xs={12} md={6} lg={4}>
                    <BsForm.Label className="small mb-1">
                      Data Limite <span className="text-danger">*</span>
                    </BsForm.Label>
                    <BsForm.Control
                      type="date"
                      size="sm"
                      value={values.dataLimite}
                      onChange={(e) => setFieldValue('dataLimite', e.target.value)}
                      isInvalid={touched.dataLimite && !!errors.dataLimite}
                    />
                    <BsForm.Control.Feedback type="invalid">
                      {errors.dataLimite}
                    </BsForm.Control.Feedback>
                  </Col>
                )}
              </Row>
            </Card.Body>
            <Card.Footer className="d-flex gap-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={isLoading}
              >
                {isLoading ? 'Gerando...' : 'Gerar Relatório'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => resetForm()}
                disabled={isLoading}
              >
                Limpar
              </Button>
            </Card.Footer>
          </Card>
        </Form>
      );
    }}
  </Formik>
);

export default FiltrosRelatorio;
